// ============================================================
// 데이터셋 가져오기 모달 공용 UI — 엑셀·카탈로그 두 모달이 공유
// ============================================================
//
// DatasetExcelImportModal(엑셀)과 DatasetCatalogImportModal(카탈로그 박제)은 단계 골격
// (원천 → 칼럼 매핑 → 미리보기 → 반영)과 스타일 결을 같이 쓴다. 여기 있는 것은 그중
// **원천과 무관한 조각**이다 — 기본값 컨트롤·미리보기 셀 표시·미리보기 단계 전체.
// 구현은 엑셀 모달이 정본이었고(카탈로그 모달이 사본을 들었다), 드리프트 실적이 쌓여
// 한 벌로 옮겼다. 원천마다 다른 것(행 수 문구·빈 결과 안내·경고 위치 문구·반영 방식)은
// 전부 props로 받는다.

import type { ReactNode } from 'react';
import type { DataDefField } from '@safetyhub/shared';
import { readonlyCellDisplay } from '@safetyhub/report-composer';
import type { DefaultValueWarning, ImportWarning } from './datasetExcelImport';

/** 미리보기에 그릴 최대 행 수 — 반영은 전 행에 한다. */
export const PREVIEW_ROW_LIMIT = 10;
/** 경고 목록 표시 상한 — 나머지는 "외 N건"으로 접는다. */
export const WARNING_LIST_LIMIT = 8;

/**
 * 원천에 연결된 칼럼 수 — 칼럼 매핑 단계 요약.
 *
 * mapping 값 형상은 원천마다 다르지만(엑셀 ColumnSource / 카탈로그 경로 문자열)
 * "미매핑 = null/undefined" 규약은 같아 그 판정만 본다.
 */
export function mappedColumnCount(
  fields: readonly DataDefField[],
  mapping: Record<string, unknown>,
): number {
  return fields.filter((f) => mapping[f.key] !== null && mapping[f.key] !== undefined).length;
}

/**
 * 미리보기 셀 표시 — 표시 규칙은 rc 정본(`readonlyCellDisplay`)을 그대로 쓴다. 여기서 더하는
 * 것은 빈 문자열을 '—'로 보이게 하는 것뿐이다(rc는 실제 표에서 빈 셀을 빈칸으로 두지만,
 * 미리보기 격자에서는 "값이 안 들어갔다"가 보여야 한다).
 */
export function previewCellText(value: unknown, field: DataDefField): string {
  if (value === '') return '—';
  return readonlyCellDisplay(value, field);
}

export interface ImportPreviewStepProps {
  fields: readonly DataDefField[];
  rows: readonly Record<string, unknown>[];
  warnings: readonly ImportWarning[];
  defaultWarnings: readonly DefaultValueWarning[];
  /** 행 수 요약 줄 — 원천마다 문구가 다르다("읽은 행 N개 · 빈 줄 M개 건너뜀" / "가져올 행 N개"). */
  summary: ReactNode;
  /** 행이 0일 때 안내 — 원천마다 원인 후보가 다르다(헤더/칼럼 연결 vs 컨텍스트). */
  emptyMessage: string;
  /** 행 경고의 위치 문구 — "표에서 N번째 줄" / "조회 결과 N번째 행". */
  warningRowLabel: (rowNumber: number) => string;
  /** 미리보기 표 아래 추가 안내(선택) — 박제 대량 행 소프트 경고 등. */
  notice?: ReactNode;
  /** 반영 방식(추가/교체) 선택 — 주면 그린다(엑셀 compose 모드는 없다). */
  applyMode?: {
    /** radio group name — 페이지에 두 모달이 동시에 뜰 일은 없지만 이름은 원천별로 유지한다. */
    radioName: string;
    existingRowCount: number;
    replace: boolean;
    onReplaceChange: (replace: boolean) => void;
  };
}

/** 미리보기 + 반영 방식 단계 — 두 모달의 마지막 단계 전체(표·경고·반영 방식). */
export function ImportPreviewStep({
  fields,
  rows,
  warnings,
  defaultWarnings,
  summary,
  emptyMessage,
  warningRowLabel,
  notice,
  applyMode,
}: ImportPreviewStepProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700">{summary}</p>

      {rows.length === 0 ? (
        <p className="rounded bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-700">
          {emptyMessage}
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50">
                {fields.map((f) => (
                  <th
                    key={f.key}
                    className="border-b border-gray-200 px-2 py-1 text-left font-medium text-gray-600"
                  >
                    {f.label || f.key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, PREVIEW_ROW_LIMIT).map((row, ri) => (
                <tr key={ri}>
                  {fields.map((f) => (
                    <td
                      key={f.key}
                      className="max-w-[12rem] truncate border-b border-gray-100 px-2 py-1 text-gray-700"
                    >
                      {previewCellText(row[f.key], f)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > PREVIEW_ROW_LIMIT ? (
        <p className="text-xs text-gray-400">
          앞 {PREVIEW_ROW_LIMIT}행만 보입니다 — 반영은 {rows.length}행 전부에 합니다.
        </p>
      ) : null}
      {notice}

      {/* 기본값 경고를 행 경고보다 위에 둔다 — 칼럼 하나가 모든 행에 걸리는 사실이라
          먼저 눈에 들어와야 한다(기본값 입력칸은 사용자가 친 원문을 그대로 보여 주므로,
          알리지 않으면 무엇이 저장됐는지 확인할 길이 없다). */}
      {defaultWarnings.length > 0 ? (
        <ul className="space-y-1 rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
          {defaultWarnings.map((w) => (
            <li key={w.fieldKey}>
              · 「{w.fieldLabel}」 기본값 <b>{w.raw || '(빈 값)'}</b> — {w.message} (모든 행에
              적용)
            </li>
          ))}
        </ul>
      ) : null}

      {warnings.length > 0 ? (
        <div>
          <p className="mb-1 text-sm font-medium text-gray-800">
            확인이 필요한 값 {warnings.length}건
          </p>
          <ul className="space-y-1 rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
            {warnings.slice(0, WARNING_LIST_LIMIT).map((w, i) => (
              <li key={`${w.rowNumber}-${w.fieldKey}-${i}`}>
                · {warningRowLabel(w.rowNumber)} 「{w.fieldLabel}」의{' '}
                <b>{w.raw || '(빈 값)'}</b> — {w.message}
              </li>
            ))}
            {warnings.length > WARNING_LIST_LIMIT ? (
              <li>· 외 {warnings.length - WARNING_LIST_LIMIT}건</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {applyMode ? (
        <div className="space-y-1.5 rounded border border-gray-200 px-3 py-2">
          <p className="text-sm font-medium text-gray-700">반영 방식</p>
          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input
              type="radio"
              name={applyMode.radioName}
              checked={!applyMode.replace}
              onChange={() => applyMode.onReplaceChange(false)}
              className="mt-0.5"
            />
            <span>
              기존 행 뒤에 추가 — 지금 {applyMode.existingRowCount}행 + {rows.length}행 ={' '}
              {applyMode.existingRowCount + rows.length}행
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input
              type="radio"
              name={applyMode.radioName}
              checked={applyMode.replace}
              onChange={() => applyMode.onReplaceChange(true)}
              className="mt-0.5"
            />
            <span>
              기존 행을 <b className="text-red-600">모두 지우고</b> 교체 —{' '}
              {applyMode.existingRowCount}행이 사라집니다
            </span>
          </label>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 미매핑 칼럼의 기본값 입력 — 칼럼 위젯에 맞는 컨트롤로 받는다.
 *
 * 값은 항상 **문자열**로 들고 있다가 셀과 같은 변환(convertCell)을 태운다. 그래야 체크박스
 * 기본값이 boolean으로, 선택 기본값이 option.value로 저장되는 경로가 한 벌로 유지된다.
 */
export function DefaultValueControl({
  field,
  value,
  onChange,
}: {
  field: DataDefField;
  value: string;
  onChange: (next: string) => void;
}) {
  const label = `${field.label || field.key} 기본값`;
  if (field.widget === 'checkbox') {
    return (
      <label className="flex w-32 shrink-0 items-center gap-1.5 text-xs text-gray-500">
        <input
          type="checkbox"
          aria-label={label}
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : '')}
        />
        기본 체크
      </label>
    );
  }
  if ((field.widget === 'select' || field.widget === 'radio') && field.options?.length) {
    return (
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-32 shrink-0 rounded border border-gray-200 bg-white px-1.5 py-1 text-xs"
      >
        <option value="">기본값 없음</option>
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  // 날짜 칼럼은 달력으로 받는다 — 값은 그대로 yyyy-MM-dd 문자열이라 convertCell 경로(텍스트
  // 통과)는 종전과 같다. 기간(date-range)은 값이 객체라 이 한 칸으로 받을 수 없으므로 여기서
  // 다루지 않는다(가져오기가 만든 문자열은 컨트롤이 시작일로 승계한다 — normalizeDateRange).
  return (
    <input
      type={field.widget === 'number' ? 'number' : field.widget === 'date' ? 'date' : 'text'}
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="모든 행 기본값"
      className="w-32 shrink-0 rounded border border-gray-200 px-1.5 py-1 text-xs"
    />
  );
}
